<ng-container *transloco="let t">
  <mat-form-field class="notes" appearance="fill" floatLabel="always">
    <mat-label>{{ t('inspection.fields.case:note') }}</mat-label>

    <textarea
      matInput
      name="notes"
      cdkTextareaAutosize
      [readonly]="readonly || printPreview"
      [formControl]="inputControl"
      [matAutocomplete]="auto"
      [drawableField]="noteInputFieldId"
      [drawableFieldTitle]="t('inspection.fields.case:note')"
      [drawableFieldReadonly]="readonly || printPreview"
      (focus)="onFocus()"
    >
    </textarea>
    <mat-autocomplete #auto="matAutocomplete">
      <mat-option *ngFor="let item of filteredOptions | async" [value]="item">
        <span>{{ item }}</span>
      </mat-option>
    </mat-autocomplete>
    <!-- Text mode buttons (inside mat-form-field) — hidden in ink mode since overlay blocks them -->
    <div matSuffix class="note-btns" *ngIf="!printPreview && !inkMode">
      <button
        mat-stroked-button
        color="primary"
        [disabled]="!inputControl.value"
        (click)="addNote($event)"
      >
        {{ t('inspection.fields.case:addNote') }}
      </button>

      <!-- AI Professionalize button (only if inspectionField exists) -->
      <button
        mat-icon-button
        *ngIf="inspectionField"
        [disabled]="!inputControl.value || aiProcessing"
        (click)="professionalizeInput()"
        [matTooltip]="t('inspection.fields.case:rewriteWithAI')"
        color="primary"
        nz-popover
        nzPopoverPlacement="topRight"
        nzPopoverTrigger="manual"
        [nzPopoverVisible]="confirmVisibleNotePopupIndex === -1"
        (nzPopoverVisibleChange)="onPopoverVisibleChange($event)"
        [nzPopoverBackdrop]="true"
        [nzPopoverContent]="nzPopoverContentTpl"
      >
        <mat-icon *ngIf="!aiProcessing">auto_fix_high</mat-icon>
        <mat-spinner
          *ngIf="aiProcessing"
          color="primary"
          [diameter]="20"
        ></mat-spinner>
      </button>

      <button
        mat-icon-button
        aria-label="notes-bank"
        color="primary"
        *ngIf="!(readonly || printPreview)"
        (click)="showNotesBank($event)"
      >
        <mat-icon>assignment</mat-icon>
      </button>
    </div>
  </mat-form-field>

  <!-- Ink mode buttons (outside mat-form-field so the drawable overlay doesn't block them) -->
  <div class="ink-note-actions" *ngIf="inkMode && !(readonly || printPreview)">
    <button
      mat-stroked-button
      color="primary"
      [disabled]="!hasInkStrokes"
      (click)="addInkNote()"
    >
      {{ t('inspection.fields.case:addNote') }}
    </button>
    <button
      mat-stroked-button
      color="warn"
      [disabled]="!hasInkStrokes"
      (click)="clearInkField()"
      [matTooltip]="t('inspection.fields.case:clearDrawing')"
    >
      {{ t('inspection.fields.case:clear') }}
    </button>
  </div>

  <ol
    *ngIf="notes.length > 0"
    class="notes-list"
    cdkDropList
    (cdkDropListDropped)="drop($event)"
    cdkDragLockAxis="y"
    [style.margin-top]="printPreview ? '10px' : inkMode ? '0' : '-20px'"
  >
    <label *ngIf="printPreview">{{
      t('inspection.fields.case:notesLabel')
    }}</label>
    <li
      *ngFor="let item of notes; let i = index"
      class="note-item"
      cdkDrag
      [cdkDragDisabled]="editableNoteIndex !== null || readonly || printPreview"
      dense
    >
      <span matLine class="note" *ngIf="i !== editableNoteIndex">
        <span class="text" *ngIf="!isInkNote(item.notes)">{{
          item.notes
        }}</span>
        <svg
          *ngIf="isInkNote(item.notes)"
          class="ink-note-display"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
        >
          <path
            *ngFor="let stroke of decodeInkNote(item.notes)"
            [attr.d]="generateSvgPath(stroke.points)"
            fill="none"
            stroke="#000"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            vector-effect="non-scaling-stroke"
          />
        </svg>
        <span class="spacer"></span>
        <div class="extra-params" *ngIf="!(readonly || printPreview)">
          <!-- AI Professionalize button for existing note (text notes only) -->
          <button
            mat-icon-button
            *ngIf="inspectionField && !isInkNote(item.notes)"
            [disabled]="aiProcessing"
            (click)="professionalizeNote(i)"
            [matTooltip]="t('inspection.fields.case:rewriteWithAI')"
            color="primary"
            nz-popover
            nzPopoverPlacement="topRight"
            nzPopoverTrigger="manual"
            [nzPopoverVisible]="confirmVisibleNotePopupIndex === i"
            (nzPopoverVisibleChange)="onPopoverVisibleChange($event)"
            [nzPopoverBackdrop]="true"
            [nzPopoverContent]="nzPopoverContentTpl"
          >
            <mat-icon *ngIf="!aiProcessing">auto_fix_high</mat-icon>
            <mat-spinner
              *ngIf="aiProcessing"
              color="primary"
              [diameter]="20"
            ></mat-spinner>
          </button>

          <button
            mat-icon-button
            aria-label="delete"
            color="primary"
            (click)="removeNote(i)"
          >
            <mat-icon>delete</mat-icon>
          </button>
          <button
            mat-icon-button
            *ngIf="!isInkNote(item.notes)"
            aria-label="edit"
            color="primary"
            (click)="editNote(i)"
          >
            <mat-icon>edit</mat-icon>
          </button>
          <mat-checkbox
            matSuffix
            [(ngModel)]="item.addNotesToReport"
            [disabled]="readonly"
            color="primary"
            class="report-note"
            (change)="onChange()"
          >
            {{ t('inspection.fields.case:attachToCertificate') }}
          </mat-checkbox>
        </div>
      </span>
      <span matLine class="note" *ngIf="i === editableNoteIndex">
        <textarea
          class="text input"
          authorityLabsAutofocus
          matInput
          cdkTextareaAutosize
          [(ngModel)]="item.notes"
        ></textarea>
        <span class="spacer"></span>
        <div class="extra-params">
          <button mat-stroked-button color="primary" (click)="applyChanges()">
            {{ t('inspection.fields.case:confirm') }}
          </button>
        </div>
      </span>
    </li>
  </ol>

  <ng-template #nzPopoverContentTpl>
    <div style="display: flex; gap: 8px; align-items: center; max-width: 600px">
      <span>{{ aiProcessedText }}</span>
      <button nz-button nzSize="small" (click)="confirm()">
        {{ t('inspection.fields.case:accept') }}
      </button>
      <button nz-button nzSize="small" (click)="cancel()">
        {{ t('inspection.fields.case:cancel') }}
      </button>
    </div>
  </ng-template>
</ng-container>
